import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Compass, Mountain, Shield, Lock } from "lucide-react";
import { usePlanState } from "@/hooks/usePlanState";

const NewExpedition = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const studentIdFromUrl = params.get("studentId");
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ student_id: studentIdFromUrl || "", topic: "", difficulty: "standard" });
  const [loading, setLoading] = useState(false);
  const { state: planState } = usePlanState();
  const isFree = !!planState && !planState.is_premium;

  useEffect(() => {
    api.get("/students").then((r) => {
      setStudents(r.data);
      if (!form.student_id && r.data.length) setForm((f) => ({ ...f, student_id: r.data[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_id) return toast.error("Add a climber first");
    if (!form.topic.trim()) return toast.error("Enter a topic or standard code");
    setLoading(true);
    try {
      const r = await api.post("/expeditions/generate", form);
      toast.success("Expedition charted!");
      navigate(`/expeditions/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate expedition");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-12 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Chart a new course</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-2">Plot the expedition</h1>
        <p className="text-mm-navy/70 mt-2">Type any concept (e.g., "long division") or a state standard code (e.g., CCSS.MATH.CONTENT.4.NBT.B.6). We'll build a five-stage climb.</p>

        <form onSubmit={submit} className="mt-8 bg-white border border-mm-navy/10 rounded-2xl p-8 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Climber</label>
            <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
              className="mt-1.5 w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-mm-steel"
              data-testid="new-exp-student-select">
              {students.map((s) => <option key={s.id} value={s.id}>{s.name} — Grade {s.grade}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Topic or standard</label>
            <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder='e.g., "long division" or CCSS.MATH.CONTENT.4.NBT.B.6'
              className="mt-1.5 w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
              data-testid="new-exp-topic-input" />
            <p className="mt-2 text-xs text-mm-navy/70 leading-relaxed" data-testid="new-exp-topic-hint">
              <b>Tip:</b> Specific topics work better than broad ones. Try <span className="font-mono text-mm-navy">Subatomic Particles</span> instead of <span className="font-mono text-mm-navy">The Atomic Theory</span>.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Route</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              <RouteCard active={form.difficulty === "rescue"} onClick={() => !isFree && setForm({ ...form, difficulty: "rescue" })}
                icon={<Shield size={20} strokeWidth={2.5} />} title="Rescue Route" body="Simpler steps & extra scaffolding" testid="route-rescue" badge="Summit Pass" locked={isFree} />
              <RouteCard active={form.difficulty === "standard"} onClick={() => setForm({ ...form, difficulty: "standard" })}
                icon={<Compass size={20} strokeWidth={2.5} />} title="Standard Trail" body="Grade-level pacing" testid="route-standard" />
              <RouteCard active={form.difficulty === "explorer"} onClick={() => !isFree && setForm({ ...form, difficulty: "explorer" })}
                icon={<Mountain size={20} strokeWidth={2.5} />} title="Explorer's Challenge" body="Deeper thinking & extensions" testid="route-explorer" badge="Summit Pass" locked={isFree} />
            </div>
            {isFree && (
              <div className="mt-3 text-xs text-mm-navy/70" data-testid="route-locked-note">
                <Lock size={11} className="inline mr-1 text-mm-gold" strokeWidth={2.5} />
                Personalized learning paths are a Summit Pass feature —{" "}
                <Link to="/pricing" className="font-bold underline hover:text-mm-navy" data-testid="route-upgrade-link">upgrade to get the support your explorer needs</Link>.
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} className="btn-trail btn-gold w-full" data-testid="new-exp-submit-btn">
            {loading ? "Charting the trail (this can take ~20s)..." : (<><Sparkles size={18} strokeWidth={2.5} /> Generate expedition</>)}
          </button>
        </form>
      </div>
    </div>
  );
};

const RouteCard = ({ active, onClick, icon, title, body, testid, badge, locked }) => (
  <button type="button" onClick={onClick} disabled={locked}
    className={`relative text-left p-4 rounded-xl border-2 transition-all ${
      locked ? "border-mm-gold/40 bg-mm-gold/5 cursor-not-allowed opacity-80" :
      active ? "border-mm-gold bg-mm-gold/10" :
      "border-mm-navy/15 bg-white hover:border-mm-steel"
    }`}
    data-testid={testid}>
    {badge && <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold text-mm-gold flex items-center gap-1">
      {locked && <Lock size={10} strokeWidth={3} />}{badge}
    </span>}
    <div className="w-9 h-9 rounded-lg bg-mm-steel/15 text-mm-navy flex items-center justify-center">{icon}</div>
    <div className="font-heading font-bold text-mm-navy mt-2">{title}</div>
    <div className="text-xs text-mm-navy/60 mt-1">{body}</div>
  </button>
);

export default NewExpedition;
